import type { Table } from "@tanstack/react-table";
import { isEmpty, isEqual } from "lodash";
import { Button, Menu, MenuItem, Divider } from "@mui/material";
import type { ButtonProps } from "@mui/material/Button";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import { useState } from "react";

import { TableStatus } from "./types";

const isDefaultView = (table: Table<any>) =>
  isEmpty(table.getState().sorting) && isEmpty(table.getState().columnFilters);

const setDefaultView = (table: Table<any>) => {
  if (isDefaultView(table)) return;

  table.resetSorting();
  table.resetColumnFilters();
};

const isRecentlyResolvedAlertsView = (table: Table<any>) =>
  isEqual(table.getState().sorting, [{ id: "last_alerted", desc: true }]) &&
  isEqual(table.getState().columnFilters, [
    { id: "status", value: [TableStatus.ALL_CLEAR] },
  ]);

const setRecentlyResolvedAlertsView = (table: Table<any>) => {
  if (isRecentlyResolvedAlertsView(table)) return;

  table.resetSorting();
  table.resetColumnFilters();
  table.getColumn("last_alerted")?.toggleSorting(true, true);
  table.getColumn("status")?.setFilterValue([TableStatus.ALL_CLEAR]);
};

const getDataViewName = (table: Table<any>) => {
  if (isDefaultView(table)) return "Data Views";
  if (isRecentlyResolvedAlertsView(table)) return "Recently Resolved Alerts";
  return "Custom View";
};

export interface Props extends ButtonProps {
  table: Table<any>;
}

export default function DataViews({
  table,
  onClick: externalOnClick,
  ...etc
}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    externalOnClick?.(event);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        {...etc}
      >
        {getDataViewName(table)}
      </Button>
      <Menu open={anchorEl !== null} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            setRecentlyResolvedAlertsView(table);
            handleClose();
          }}
        >
          {isRecentlyResolvedAlertsView(table) && <CheckIcon sx={{ mr: 1 }} />}
          Recently Resolved Alerts
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={isDefaultView(table)}
          onClick={() => {
            setDefaultView(table);
            handleClose();
          }}
        >
          Reset
        </MenuItem>
      </Menu>
    </>
  );
}
